import React, { useEffect, useState } from 'react';
import {
  Box, Paper, Typography, TextField, Button, Grid, InputLabel, FormControl, FormHelperText, Avatar, CircularProgress
} from '@mui/material';
import { PhotoCamera } from '@mui/icons-material';

const allowedImageTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/x-icon', 'image/vnd.microsoft.icon'];

const fetchSettings = async () => {
  const res = await fetch('/api/project-settings');
  if (!res.ok) throw new Error('Failed to fetch settings');
  return res.json();
};

const updateSettings = async (formData) => {
  const res = await fetch('/api/project-settings', {
    method: 'POST',
    body: formData,
  });
  return res.json();
};

// Utility function to update favicon dynamically
function updateFavicon(url) {
  let link = document.querySelector("link[rel~='icon']");
  if (!link) {
    link = document.createElement('link');
    link.rel = 'icon';
    document.head.appendChild(link);
  }
  link.href = url;
}

function ApplicationSettings() {
  const [form, setForm] = useState({
    project_name: '',
    project_title: '',
    contact_email: '',
  });
  const [logo, setLogo] = useState(null);
  const [logoPreview, setLogoPreview] = useState('');
  const [landingImage, setLandingImage] = useState(null);
  const [landingPreview, setLandingPreview] = useState('');
  const [favicon, setFavicon] = useState(null);
  const [faviconPreview, setFaviconPreview] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    setLoading(true);
    try {
      const data = await fetchSettings();
      setForm({
        project_name: data.project_name || '',
        project_title: data.project_title || '',
        contact_email: data.contact_email || '',
      });
      setLogoPreview(data.project_logo_url || '');
      setLandingPreview(data.landing_page_image_url || '');
      setFaviconPreview(data.favicon_url || '');
    } catch (e) {
      setError('Failed to load settings');
    }
    setLoading(false);
  };

  const handleInputChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleFileChange = (e, setter, previewSetter) => {
    const file = e.target.files[0];
    if (file && !allowedImageTypes.includes(file.type)) {
      setError('Invalid file type. Only PNG, JPG, JPEG, ICO allowed.');
      setter(null);
      previewSetter('');
      return;
    }
    setter(file);
    if (file) {
      const reader = new FileReader();
      reader.onload = (ev) => previewSetter(ev.target.result);
      reader.readAsDataURL(file);
    } else {
      previewSetter('');
    }
  };

  const validate = () => {
    if (!form.project_name.trim() || !form.project_title.trim()) {
      setError('Project Name and Title are required.');
      return false;
    }
    if (form.contact_email && !/^\S+@\S+\.\S+$/.test(form.contact_email)) {
      setError('Invalid email format.');
      return false;
    }
    setError('');
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSuccess('');
    if (!validate()) return;
    setSubmitting(true);
    const formData = new FormData();
    formData.append('project_name', form.project_name);
    formData.append('project_title', form.project_title);
    formData.append('contact_email', form.contact_email);
    if (logo) formData.append('project_logo', logo);
    if (landingImage) formData.append('landing_page_image', landingImage);
    if (favicon) formData.append('favicon', favicon);
    try {
      const res = await updateSettings(formData);
      if (res.error) setError(res.error);
      else {
        setSuccess('Settings updated successfully!');
        setLogoPreview(res.project_logo_url || '');
        setLandingPreview(res.landing_page_image_url || '');
        setFaviconPreview(res.favicon_url || '');
        // Dynamically update favicon
        if (res.favicon_url) updateFavicon(res.favicon_url);
        // Dynamically update app name in browser tab
        if (res.project_name) document.title = res.project_name;
        // Optionally, update logo in a global state/context if used elsewhere
      }
    } catch (e) {
      setError('Failed to update settings');
    }
    setSubmitting(false);
  };

  const handleReset = () => {
    setForm({ project_name: '', project_title: '', contact_email: '' });
    setLogo(null); setLogoPreview('');
    setLandingImage(null); setLandingPreview('');
    setFavicon(null); setFaviconPreview('');
    setError(''); setSuccess('');
  };

  if (loading) return <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>;

  return (
    <Box sx={{ p: { xs: 1, md: 4 }, maxWidth: 800, mx: 'auto' }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Typography variant="h6" fontWeight="bold" sx={{ mb: 3 }}>
          Update Project Settings
        </Typography>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Project Name"
                name="project_name"
                value={form.project_name}
                onChange={handleInputChange}
                placeholder="Project Name"
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Project Title"
                name="project_title"
                value={form.project_title}
                onChange={handleInputChange}
                placeholder="Project Title"
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel shrink>Project Logo</InputLabel>
                <Button variant="outlined" component="label" startIcon={<PhotoCamera />} sx={{ mb: 1 }}>
                  Choose File
                  <input type="file" accept="image/png,image/jpeg,image/jpg,image/x-icon" hidden onChange={e => handleFileChange(e, setLogo, setLogoPreview)} />
                </Button>
                {logoPreview && <Avatar src={logoPreview} variant="rounded" sx={{ width: 56, height: 56, mt: 1 }} />} 
                <FormHelperText>Accepts PNG, JPG, JPEG, ICO</FormHelperText>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel shrink>Project Landing Page Image</InputLabel>
                <Button variant="outlined" component="label" startIcon={<PhotoCamera />} sx={{ mb: 1 }}>
                  Choose File
                  <input type="file" accept="image/png,image/jpeg,image/jpg,image/x-icon" hidden onChange={e => handleFileChange(e, setLandingImage, setLandingPreview)} />
                </Button>
                {landingPreview && <Avatar src={landingPreview} variant="rounded" sx={{ width: 56, height: 56, mt: 1 }} />} 
                <FormHelperText>Accepts PNG, JPG, JPEG, ICO</FormHelperText>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel shrink>Project Favicon</InputLabel>
                <Button variant="outlined" component="label" startIcon={<PhotoCamera />} sx={{ mb: 1 }}>
                  Choose File
                  <input type="file" accept="image/png,image/jpeg,image/jpg,image/x-icon" hidden onChange={e => handleFileChange(e, setFavicon, setFaviconPreview)} />
                </Button>
                {faviconPreview && <Avatar src={faviconPreview} variant="rounded" sx={{ width: 40, height: 40, mt: 1 }} />} 
                <FormHelperText>Accepts PNG, JPG, JPEG, ICO</FormHelperText>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Landing Page Email"
                name="contact_email"
                value={form.contact_email}
                onChange={handleInputChange}
                placeholder="Enter Email Address"
                type="email"
              />
            </Grid>
            <Grid item xs={12} sx={{ textAlign: 'center', mt: 2 }}>
              <Button type="submit" variant="contained" color="primary" sx={{ mr: 2, px: 4 }} disabled={submitting}>
                Update
              </Button>
              <Button type="button" variant="contained" color="error" sx={{ px: 4 }} onClick={handleReset}>
                Reset
              </Button>
            </Grid>
            {error && (
              <Grid item xs={12}>
                <Typography color="error" sx={{ mt: 1 }}>{error}</Typography>
              </Grid>
            )}
            {success && (
              <Grid item xs={12}>
                <Typography color="success.main" sx={{ mt: 1 }}>{success}</Typography>
              </Grid>
            )}
          </Grid>
        </form>
      </Paper>
    </Box>
  );
}

export default ApplicationSettings; 